import { IObservableValue } from "mobx";

import { IconDefinition } from "@fortawesome/free-solid-svg-icons";
import { Ti18nKeys } from "./constants/i18n";
import { TRewardBenefitType } from "./TypesNEW";
import { EHyperpayPaymentStatus } from "./constants/enum/hyperpay-payment-status";

export type StringOrNumber = string | number;

export type TStatusExtraData = { TBW_ids: number[]; temporaryBanDays: number };

export enum E_ADMIN_ROLE {
  KHATTABA_ADMIN = "Khattaba Admin",
  KHATTABA_ADMIN_AI = "Khattaba Admin Ai",
  MARKETING = "Marketing",
  DESIGNER = "Designer",
  SENIOR_QA = "Senior Quality Assurance",
  DEVELOPER = "Developer",
  CUSTOMER_SUPPORT = "Customer Support",
  QA = "Quality Assurance",
  BOT = "Bot",
  SHAREHOLDERS = "Shareholders",
  CTO = "CTO",
  CEO = "CEO",
  NO_ROLE = ""
}

export type TAdminRole = `${E_ADMIN_ROLE}`;

export type TSignInData = {
  id: number;
  username: string;
  lastLogin: string;
  role: TAdminRole;
  fullName: string;
  adminPermissions: {
    moduleId: number;
    description: string;
    hasPermission: 0 | 1;
  }[];
  needResetPassword: boolean;
};

export type TAdmin = {
  id: number;
  username: string;
  fullName: string;
  role: TAdminRole;
  lastLogin: string;
  needResetPassword: boolean;
};
export type TOption = {
  title: string;
  link: string;
  name: TPermissionName;
  pageName: TCorePage;
  pageSubName: string;
  visibility: boolean;
};
export type TPickerOption = {
  label: StringOrNumber;
  value: StringOrNumber;
};

export type TPicker2Option = {
  label: string;
  value: string;
};

export type TEditPopup = {
  options: TPickerOption[];
  existingValue: StringOrNumber;
  key: string;
};

export type TDataRow = {
  [key: string]: StringOrNumber;
};

export type TDataLinkProps = {
  name:
    | "USER_PAYMENTS"
    | "USER_MESSAGES"
    | "USER_EVENTS"
    | "EDIT_USER_DATA"
    | "CONSUMABLES";
  onPress: Function;
  type: "text" | "button";
};

export type TBadgeProps = {
  color: string;
  textColor: string;
  content: number | string;
  style: React.CSSProperties;
};

export type TInputFieldWithLabelProps = {
  id?: string;
  label: StringOrNumber;
  // input-focus for adding shadow to the field
  // input-placeholder for field color
  fieldClassName:
    | "input"
    | "input-focus input-placeholder"
    | "input-placeholder"
    | "et3-field-input";
  style: React.CSSProperties;
  value: StringOrNumber;
  onChoose: (a: StringOrNumber) => void;
  onBlur: (a: StringOrNumber) => void;
  type: "number" | "text" | "password" | "textarea";
  onEnter: Function;
};

export type TMenuItemProps = {
  title: string;
  icon: IconDefinition;
  link: string;
  visible: boolean;
};
export type TMenuDropDownItemProps = {
  icon: IconDefinition;
  title: string;
  options: TOption[];
  isMenuOpenObservable: IObservableValue<boolean>;
  visible: boolean;
  onPress: () => {};
};
export type TSubMenuDropDownProps = {
  options: TOption[];
};
export type TAnimatedDropdown = {
  options: TOption[];
  isMenuOpen: boolean;
};
export type TSubMenuDropDownItemProps = {
  option: TOption;
  visible: boolean;
  isSelected: boolean;
};
export interface TPickerProps {
  options: TPickerOption[];
  value: StringOrNumber | string[];
  onChoose: (value: unknown) => void;
  style: React.CSSProperties;
}

export type TPageTitleProps = {
  title: string;
  subTitle: string;
  withRefresh: boolean;
  withHelper: boolean;
  onPress: () => void;
};

export type TableColumnInitial = {
  text: string;
  dataField: string;
};

export type TExpectedTypeFromDataBase =
  | StringOrNumber
  | object
  | boolean
  | StringOrNumber[]
  | boolean[];

export type TableData = { [k in string]: TExpectedTypeFromDataBase }[];

export type TNewTable = {
  title: string;
  width: number;
  columns: TColumnsWithWidth;
  pageSize: number;
  data: TableData;
  isColored: boolean;
  shouldBeFlagged: boolean;
  showCSVOptions: boolean;
};
export type TDataGridType = {
  columns: TColumnsWithWidth;
  pageSize: number;
  data: TableData;
  isColored: boolean;
  shouldBeFlagged: boolean;
  disableCSVOptions: boolean;
  totalDatawidth: number;
};

export type TColumnsWithWidth = {
  headerName: string;
  field: string;
  width: number;
  sortable: boolean;
  filterable: boolean;
}[];

export type TTableHeader = {
  title: string;
  width: number;
  isCollapsible: boolean;
  sectionId: string;
  defaultOpen: boolean;
  rowsCount: number;
};

export type TTableDataType =
  | "DATA"
  | "POOL_APPROVAL"
  | "POOL_REPORT"
  | "MESSAGE_DATA"
  | "DEVICE_DATA"
  | "PERMISSIONS"
  | "TBW_DATA"
  | "INVESTIGATION_CASES"
  | "USER_EVENTS_TABLE"
  | "ADMIN_EVENTS"
  | "AUDITED_DATA"
  | "EFFECTIVENESS_TABLE_DATA"
  | "SUPERVISE_ADMIN_AUDITS"
  | "WORKLOAD_TABLE_DATA"
  | "EDIT_PROFILE_DATA"
  | "EDIT_PROFILE_DATA_ARCHIVE"
  | "MY_CSR_EFFECTIVENESS_TABLE_DATA";

export type TTableSearchInput = {
  [k: string]: { searchText: string; sensitiveCaseState: boolean };
};

export type TDataBoxHeadProps = {
  title: string;
  tableId: string;
  data: TableData;
  convertToCsv: boolean;
};

export type TMessage = {
  message: string;
  messageId: number;
  messageObject: {
    message: string;
    messageType: string;
  };
  messageType: "text" | "picture" | "voice";
  recipientId: number;
  senderId: number;
  sentOn: string;
  isDeleted: E_MESSAGE_DELETION_STATUS;
  deletedOn: string;
  reactions: TMessageReaction[];
  hasReactionHistory: boolean;
};

export type TUserDetails = {
  userDetails: TUserEntry;
};

export type TUserInformations = {
  username: string;
  user_id: number;
  userUUID: string;
  country: string;
  last_login: string;
  gender: TGender;
  is_vip: 1 | 0;
  isSilentBanned: number;
};

export type TUserApproveImagesDetails = {
  user_id: number;
  userUUID: string;
  albumPicture: [
    {
      id: number;
      picture: string;
      status: string;
      selfieVerified: string;
      isActive: 0 | 1;
    }
  ];
};

export type TUserEntry = {
  user_id: number;
  userUUID: string;
  country: string;
  description: string;
  descriptionID: number;
  descriptionStatus: TDescrptionStatus;
  descriptionLanguage: string;
  descriptionIsGeneratedByAi: number;
  peerDescriptionID: number;
  peerDescription: string;
  peerDescriptionStatus: TDescrptionStatus;
  peerDescriptionLanguage: string;
  gender: TGender;
  username: string;
  last_login: string;
  is_vip: 1 | 0;
  isSilentBanned: number;
  userUnderCSRReport: number;

  albumPicture: [
    {
      id: number;
      picture: string;
      status: string;
      selfieVerified: string;
      isActive: 0 | 1;
    }
  ];
};

export type TApprovalAlbumPicture = {
  id: number;
  picture: string;
  status: string;
  selfieVerified: string;
  isActive: 0 | 1;
};

export type TDescrptionStatus = "approved" | "pending" | "not_filled";

export type TReportSubjectsDetailsProps = {
  report: TReport;
  status: string;
  pageName: TCorePage;
  pendingReport: boolean;
};
export type TReportsListItemProps = {
  report: TReport;
  status: string;
  pageName: TCorePage;
  takenActionType: string;
  eventID: number;
  date: string;
  reportNumberFromPartition: number;
  reportsPartitionMaxNumber: number;
};

export type TReport = {
  reportId: number;
  status: TStatus;
  reason1: 1 | 0;
  reason2: 1 | 0;
  reason3: 1 | 0;
  reason4: 1 | 0;
  reason5: 1 | 0;
  comment: string;
  reportedOn: string;

  reporterId: number;
  reporterUUID: string;
  reporterUsername: string;
  reporterGender: TGender;
  reporterCreatedOn: string;
  reporterLoginCount: number;

  reportedId: number;
  reportedUUID: string;
  reportedUsername: string;
  reportedGender: TGender;
  reportedCreatedOn: string;
  reportedLoginCount: number;
  reportedIsSilentBanned: number;

  conversation: any;
  reporterPaid: {
    isReporterPaidVIP: boolean;
    isReporterPaidPlus: boolean;
    isReporterPaidAlkhattabat: boolean;
    isReporterPaidBoost: boolean;
    isReporterPaidRewind: boolean;
    isReporterPaidSilver: boolean;
    isReporterPaidGold: boolean;
    isReporterPaidBlack: boolean;
    isReporterPaidPlatinum: boolean;
    isReporterPaidPower: boolean;
    isReporterSubscribedVIP: boolean;
    isReporterSubscribedPlus: boolean;
    isReporterSubscribedAlkhattabat: boolean;
    isReporterSubscribedBoost: boolean;
    isReporterSubscribedRewind: boolean;
    isReporterSubscribedSilver: boolean;
    isReporterSubscribedGold: boolean;
    isReporterSubscribedBlack: boolean;
    isReporterSubscribedPlatinum: boolean;
    isReporterSubscribedPower: boolean;
    wasReporterStripeUser: boolean;
  };
  reportedPaid: {
    isReportedPaidVIP: boolean;
    isReportedPaidPlus: boolean;
    isReportedPaidAlkhattabat: boolean;
    isReportedPaidBoost: boolean;
    isReportedPaidRewind: boolean;
    isReportedPaidSilver: boolean;
    isReportedPaidGold: boolean;
    isReportedPaidBlack: boolean;
    isReportedPaidPlatinum: boolean;
    isReportedPaidPower: boolean;
    isReportedSubscribedVIP: boolean;
    isReportedSubscribedPlus: boolean;
    isReportedSubscribedAlkhattabat: boolean;
    isReportedSubscribedBoost: boolean;
    isReportedSubscribedRewind: boolean;
    isReportedSubscribedSilver: boolean;
    isReportedSubscribedGold: boolean;
    isReportedSubscribedBlack: boolean;
    isReportedSubscribedPlatinum: boolean;
    isReportedSubscribedPower: boolean;
    wasReportedStripeUser: boolean;
  };
  reporterTempBanCount: number;
  reportedTempBanCount: number;
  reporterWarnCount: number;
  reportedWarnCount: number;
  reporterTester: boolean;
  reportedTester: boolean;
  isReportIndonesianMalaysian: boolean;
};

export type TPendingTReports = {
  [reportedId in string]: { data: TReport[]; total: number };
};

export type TUserPendingReports = {
  userReports: TPendingTReports;
  pool: TPoolTable;
};

export type AuditReportData = {
  id: number;
  date: string;
  userId: number;
  gender: string;
  username: string;
  type: string;
  extraData: string;
  isSeen: string;
  reportID: number;
  pageName: TCorePage;
  tbw_reasons: object;
  reportData: TReport;
};

export type TEventsList = {
  id: number;
  date: string;
  userId: number;
  userUUID: string;
  gender: "m" | "f" | "unknown";
  username: string;
  type: string;
  extraData: string;
  isSeen: "not_seen" | "seen";
  reportID: string;
  pageName: TCorePage;
  reportData: TReport;
  imageID: string;
  tbw_reasons: object;
}[];

export type TGender = "m" | "f" | "";

export type TReportDetailProps = {
  isDetailVisible: boolean;
  report: TReport;
  status: string;
  pageName: TCorePage;
};

export type TUserSearchForm = {
  onClickAction: Function;
  disabled: boolean;
  withLimit: boolean;
  withPlatform: boolean;
};

export type TReportUserDetails = {
  id: number;
  username: string;
  gender: TGender;
  createdOn: string;
  loginCount: number;
  tempBanCount: number;
  warnCount: number;
};

export type TUserDetailsObject = {
  user: TReportUserDetails;
};

export type TDataBarChart = {
  title: string;
  dataLabels: string[];
  values: number[];
};
export type TUserKeys =
  | "userId"
  | "username"
  | "gender"
  | "religion"
  | "subreligion"
  | "status"
  | "isSilentBanned"
  | "silentBannedOn"
  | "statusChangeDate"
  | "facebookVerify"
  | "smsVerify"
  | "emailVerify"
  | "appleVerify"
  | "emailNotification"
  | "pushNotification"
  | "description"
  | "descriptionStatus"
  | "createdOn"
  | "createdFrom"
  | "originCountry"
  | "residenceCountry"
  | "cityName"
  | "selfieVerificationCompletedOn"
  | "numberOfVerifiedApprovedPictures"
  | "loginCount"
  | "state"
  | "ipAddress"
  | "verificationStatus"
  | "verificationCode"
  | "referralCode"
  | "mobileAppPlatform"
  | "mobileAppVersion"
  | "deviceId"
  | "device_token"
  | "isFlagged"
  | "reportsSent"
  | "reportsReceived"
  | "temporaryBannedCount"
  | "warningCount"
  | "wasPaying"
  | "lastLogin"
  | "freemiumVIPUntil"
  | "payingVIPUntil"
  | "payingPLUSUntil"
  | "payingKhattabatUntil"
  | "userAppLanguage"
  | "deactivatedOn"
  | "profilePublicID"
  | "phoneDetails"
  | "freemiumPLUSUntil"
  | "khattabaAssigned"
  | "freemiumSilverUntil"
  | "payingSilverUntil"
  | "freemiumGoldUntil"
  | "payingGoldUntil"
  | "freemiumBlackUntil"
  | "payingBlackUntil"
  | "freemiumPlatinumUntil"
  | "payingPlatinumUntil"
  | "payingPowerUntil";

export type TUser = {
  mainPictureIndex?: number;
  userId: number;
  userUUID: string;
  username: string;
  gender: string;
  religion: string;
  subreligion: string;
  status: string;
  isSilentBanned: number;
  silentBannedOn: string;
  statusChangeDate: string;
  facebookVerify: string;
  smsVerify: string;
  emailVerify: string;
  appleVerify: string;
  emailNotification: string;
  pushNotification: string;
  descriptionID: number;
  description: string;
  descriptionStatus: string;
  descriptionLanguage: string;
  descriptionIsGeneratedByAi: number;
  peerDescriptionID: number;
  peerDescription: string;
  peerDescriptionStatus: string;
  peerDescriptionLanguage: string;
  createdOn: string;
  createdFrom: string;
  originCountry: string;
  residenceCountry: string;
  cityName: string;
  selfieVerificationCompletedOn: string;
  numberOfVerifiedApprovedPictures: number;
  loginCount: number;
  state: string;
  ipAddress: string;
  verificationStatus: string;
  verificationCode: string;
  referralCode: string;
  mobileAppPlatform: string;
  mobileAppVersion: string;
  deviceId: string;
  device_token: string;
  isFlagged: number;
  reportsSent: number;
  reportsReceived: number;
  temporaryBannedCount: number;
  warningCount: number;
  userUnderCSRInvestigation: boolean;
  userUnderInvestigationAndReadyToResolve: boolean;
  userUnderCSRReport: number;
  wasPaying: boolean;
  wasStripeUser: boolean;
  isPaidVIP: boolean;
  isPaidPlus: boolean;
  isPaidAlkhattabat: boolean;
  isPaidBoost: boolean;
  isPaidRewind: boolean;
  isPaidSilver: boolean;
  isPaidGold: boolean;
  isPaidBlack: boolean;
  isPaidPlatinum: boolean;
  isPaidPower: boolean;
  isSubscribedVIP: boolean;
  isSubscribedPlus: boolean;
  isSubscribedAlkhattabat: boolean;
  isSubscribedBoost: boolean;
  isSubscribedRewind: boolean;
  isSubscribedSilver: boolean;
  isSubscribedGold: boolean;
  isSubscribedBlack: boolean;
  isSubscribedPlatinum: boolean;
  isSubscribedPower: boolean;
  lastLogin: number;
  freemiumVIPUntil: string;
  freemiumPLUSUntil: string;
  freemiumSilverUntil: string;
  freemiumGoldUntil: string;
  freemiumBlackUntil: string;
  freemiumPlatinumUntil: string;
  payingKhattabatUntil: string;
  payingVIPUntil: string;
  payingPLUSUntil: string;
  payingBOOSTUntil: string;
  payingREWINDUntil: string;
  payingSilverUntil: string;
  payingGoldUntil: string;
  payingBlackUntil: string;
  payingPlatinumUntil: string;
  payingPowerUntil: string;
  userAppLanguage: string;
  pictures: TAlbum[];
  comments: TComment[];
  commentsNumber: number;
  isTester: boolean;
  deactivatedOn: string;
  deactivationCounter: number;
  profilePublicID: string;
  phoneDetails: string;
  khattabaAssigned: string;
  hasAccessToBoosts: boolean;
  hasAccessToRewinds: boolean;
  hasAccessToReadReceipts: boolean;
  hasAccessToNewConversationsDaily: boolean;
  hasAccessToNewConversationsWeekly: boolean;
  hasAccessToOpenUnlimitedConversations: boolean;
  hasAccessToLikes: boolean;
  hasAccessToLikesDaily: boolean;
  hasRemainingBoosts: boolean;
  hasRemainingRewinds: boolean;
  hasRemainingReadReceipts: boolean;
  hasRemainingNewConversationsDaily: boolean;
  hasRemainingNewConversationsWeekly: boolean;
  hasRemainingLikes: boolean;
  hasRemainingLikesDaily: boolean;
  emails: {
    userEmail: string;
    verificationPlatform: string;
    verificationActive: number;
    userActivityEmailsSubscription: number;
    promotionalEmailsSubscription: number;
    newMessageEmailsSubscription: number;
    visitProfileEmailsSubscription: number;
    likeEmailsSubscriptio: number;
    userActivityEmailsSettingsOn: number;
    promotionalEmailsSettingsOn: number;
    newMessageEmailsSettingsOn: number;
    visitProfileEmailsSettingsOn: number;
    likeEmailsSettingsOn: number;
  }[];
  quizData: TUserQuestionsAnswers[];
};
export type TUserFromServer = {
  userId: number;
  userUUID: string;
  username: string;
  statusChangeDate: string;
  gender: string;
  religion: string;
  subreligion: string;
  status: string;
  isSilentBanned: number;
  silentBannedOn: string;
  facebookVerify: string;
  smsVerify: string;
  emailVerify: string;
  appleVerify: string;
  emailNotification: string;
  pushNotification: string;
  descriptionID: number;
  description: string;
  descriptionStatus: string;
  descriptionLanguage: string;
  descriptionIsGeneratedByAi: number;
  peerDescriptionID: number;
  peerDescription: string;
  peerDescriptionStatus: string;
  peerDescriptionLanguage: string;
  createdOn: string;
  createdFrom: string;
  originCountry: string;
  residenceCountry: string;
  cityName: string;
  selfieVerificationCompletedOn: string;
  numberOfVerifiedApprovedPictures: number;
  loginCount: number;
  state: string;
  ipAddress: string;
  verificationStatus: string;
  verificationCode: string;
  referralCode: string;

  mobileAppPlatform: string;
  mobileAppVersion: string;
  deviceId: string;
  device_token: string;
  isFlagged: number;
  reportsSent: number;
  reportsReceived: number;
  temporaryBannedCount: number;
  warningCount: number;
  userUnderCSRInvestigation: boolean;
  userUnderInvestigationAndReadyToResolve: boolean;
  userUnderCSRReport: number;
  wasPaying: boolean;
  wasStripeUser: boolean;
  isPaidVIP: boolean;
  isPaidPlus: boolean;
  isPaidAlkhattabat: boolean;
  isPaidBoost: boolean;
  isPaidRewind: boolean;
  isPaidSilver: boolean;
  isPaidGold: boolean;
  isPaidBlack: boolean;
  isPaidPlatinum: boolean;
  isPaidPower: boolean;
  isSubscribedVIP: boolean;
  isSubscribedPlus: boolean;
  isSubscribedAlkhattabat: boolean;
  isSubscribedBoost: boolean;
  isSubscribedRewind: boolean;
  isSubscribedSilver: boolean;
  isSubscribedGold: boolean;
  isSubscribedBlack: boolean;
  isSubscribedPlatinum: boolean;
  isSubscribedPower: boolean;
  lastLogin: number;
  freemiumVIPUntil: string;
  freemiumPLUSUntil: string;
  freemiumSilverUntil: string;
  freemiumGoldUntil: string;
  freemiumBlackUntil: string;
  freemiumPlatinumUntil: string;
  payingKhattabatUntil: string;
  payingVIPUntil: string;
  payingPLUSUntil: string;
  payingBOOSTUntil: string;
  payingREWINDUntil: string;
  payingSilverUntil: string;
  payingGoldUntil: string;
  payingBlackUntil: string;
  payingPlatinumUntil: string;
  payingPowerUntil: string;
  userAppLanguage: string;
  deactivatedOn: string;
  deactivationCounter: number;
  isTester: boolean;
  profilePublicID: string;
  phoneDetails: string;
  khattabaAssigned: string;
  hasAccessToBoosts: boolean;
  hasAccessToRewinds: boolean;
  hasAccessToReadReceipts: boolean;
  hasAccessToNewConversationsDaily: boolean;
  hasAccessToNewConversationsWeekly: boolean;
  hasAccessToOpenUnlimitedConversations: boolean;
  hasRemainingBoosts: boolean;
  hasRemainingRewinds: boolean;
  hasRemainingReadReceipts: boolean;
  hasRemainingNewConversationsDaily: boolean;
  hasRemainingNewConversationsWeekly: boolean;
  hasAccessToLikes: boolean;
  hasAccessToLikesDaily: boolean;
  hasRemainingLikes: boolean;
  hasRemainingLikesDaily: boolean;
  emails: {
    userEmail: string;
    verificationPlatform: string;
    verificationActive: number;
    userActivityEmailsSubscription: number;
    promotionalEmailsSubscription: number;
    newMessageEmailsSubscription: number;
    visitProfileEmailsSubscription: number;
    likeEmailsSubscriptio: number;
    userActivityEmailsSettingsOn: number;
    promotionalEmailsSettingsOn: number;
    newMessageEmailsSettingsOn: number;
    visitProfileEmailsSettingsOn: number;
    likeEmailsSettingsOn: number;
  }[];
};

export type TCorePage =
  | ""
  | "CORE_LOGIN"
  | "ALARMS"
  | "USER_ADVANCED"
  | "SILENTLY_BANNED_USERS"
  | "USER_CONVERSATIONS"
  | "USER_DEVICES"
  | "USER_EDIT_PROFILE"
  | "USER_EVENTS"
  | "USER_LOGIN"
  | "USER_MESSAGES"
  | "USER_MESSAGE_ID"
  | "USER_PAYMENTS"
  | "USER_REFERRAL_CODE_USAGE"
  | "USER_PROMO_CODE_USAGE"
  | "APPROVAL_DATA"
  | "REPORTS"
  | "INVESTIGATION_CASES"
  | "CSR_MY_PERFORMANCE"
  | "CSR_MY_MISTAKES"
  | "CSR_MY_PREV_ACTIONS"
  | "AUDITOR_AUDIT"
  | "AUDITOR_AUDITED_EVENTS"
  | "AUDITOR_MY_AUDITING_PERFORMANCE"
  | "AUDITOR_MY_AUDITING_MISTAKES"
  | "AUDITOR_OTHER_CSR_PERFORMANCE"
  | "AUDITOR_EVENTS_CHARTS"
  | "AUDITOR_POOL"
  | "AUDITOR_WORKLOAD_DATA"
  | "AUDITOR_PROMO_CODE"
  | "AUDITOR_WEB_PAYMENTS"
  | "AUDITOR_USER_RATE_US_DATA"
  | "SUPERVISOR_PERMISSION"
  | "SUPERVISOR_SUPERVISE"
  | "SUPERVISOR_SUPERVISED_EVENTS"
  | "SUPERVISOR_RESET_PASSWORDS"
  | "SUPERVISOR_FLAGGED_DESCRIPTIONS"
  | "COMMUNICATIONS_ACCOUNTS_DELETION"
  | "COMMUNICATIONS_PROMO_CODE_GENERATOR"
  | "COMMUNICATIONS"
  | "QA_CHANGE_STATUS_DATE"
  | "QA_CHANGE_EMAIL_SUBSCRIPTION"
  | "QA_OBTAIN_IOS_RECEIPT_TOKENS"
  | "CHANGE_PASSWORD"
  | "DEVELOPER_ONE_O_ONE_M"
  | "DEVELOPER_FLAGGED_CONNECTIONS"
  | "DEVELOPER_ON_CALL"
  | "DEVELOPER_STATS"
  | "DEVELOPER_ON_CALL_TEAM"
  | "DEVELOPER_WATCH_ALERT"
  | "DEVELOPER_BLOCKED_REQUESTS"
  | "DEVELOPER_FLUSH_HOSTS"
  | "DEVELOPER_REASSIGN_KHATTABA"
  | "SHARE_HOLDER"
  | "DEVELOPER_TURN_OFF_ALARMS"
  | "KHATTABAT_M"
  | "KHATTABAT_F"
  | "KHATTABAT_CLIENTS"
  | "KHATTABAT_MATCHING_RESULTS"
  | "KHATTABAT_MATCHING_SEARCH_AND_1O1M"
  | "SUPERVISOR_RECEIPT_COMPENSATION"
  | "SUPERVISOR_RECEIPT_CONFIGURATION"
  | "CSR_MY_RECEIPT_EVENT_COUNT"
  | "KHATTABAT_REMUNERATION"
  | "SCSR_GIVE_SUBSCRIPTIONS"
  | "AUDITOR_EVENTS_DATA"
  | "SUPERVISOR_TURN_ON_OFF_KHATTABAT"
  | "KHATTABAT_DATA_COLLECTION"
  | "KHATTABAT_NEW_CLIENTS"
  | "DEVELOPER_DEBUG_IMAGE_AI"
  | "USER_SELFIE_VERIFICATION_PHOTOS"
  | "USER_AI_DESCRIPTIONS"
  | "SUPERVISOR_KHATTABAT_FUNNEL_STATS"
  | "KHATTABAT_MATCHMAKING_AUDIT"
  | "DEVELOPER_ADMIN_USAGE_DATA"
  | "DEVELOPER_AI_GENERATED_DESCRIPTION_ALL"
  | "DEVELOPER_TRANSFER_USER_PACKAGE"
  | "SUPERVISOR_OTHER_AUDITORS_PERFORMANCE"
  | "AUDITOR_REPORT_ACTIONS_ANALYTICS"
  | "USER_CONSUMABLES";

export type TAdminTable =
  | "COMMENTS"
  | "USER_ADVANCED_TABLE"
  | "SILENTLY_BANNED_USERS"
  | "USER_ADVANCED_QUIZ_TABLE"
  | "EMAILS"
  | "MESSAGES_SENT_BETWEEN"
  | "USER_DEVICES_TABLE"
  | "EDIT_PROFILE"
  | "EDIT_PROFILE_HISTORY"
  | "USER_BREACHES_TABLE"
  | "USER_EVENTS_TABLE"
  | "USER_EMAIL_EVENTS_TABLE"
  | "DEACTIVATION_ACTIVITY"
  | "USER_LOGIN_TABLE"
  | "USER_MESSAGES_TABLE"
  | "USER_MESSAGES_ID_TABLE"
  | "SILVER_SUBSCRIPTION_HISTORY_FOR"
  | "GOLD_SUBSCRIPTION_HISTORY_FOR"
  | "BLACK_SUBSCRIPTION_HISTORY_FOR"
  | "PLATINUM_SUBSCRIPTION_HISTORY_FOR"
  | "POWER_BUNDLE_SUBSCRIPTION_HISTORY_FOR"
  | "READ_SUBSCRIPTION_HISTORY_FOR"
  | "VIP_SUBSCRIPTION_HISTORY_FOR"
  | "PLUS_SUBSCRIPTION_HISTORY_FOR"
  | "KHATTABAT_SUBSCRIPTION_HISTORY_FOR"
  | "REWIND_SUBSCRIPTION_HISTORY_FOR"
  | "BOOST_SUBSCRIPTION_HISTORY_FOR"
  | "USER_REFERRAL_CODE_USAGE_TABLE"
  | "USER_PROMO_CODE_USAGE_TABLE"
  | "SELFIE_VERIFICATION_PHOTOS"
  | "AI_DESCRIPTIONS"
  | "AI_GENERATED_DESCRIPTION_ALL"
  | "PERFORMANCE"
  | "EFFECTIVENESS"
  | "EVENTS_COUNT"
  | "MY_CSR_PREV_ACTIONS"
  | "CURRENT_RECEIPT_MONTH"
  | "EVENTS_LIST"
  | "AUDITORS_EFFICIENCY"
  | "AUDITING_PERFORMANCE"
  | "AUDITING_EFFICIENCY"
  | "DAILY_STATS"
  | "APPROVAL_POOL_TABLE"
  | "REPORT_POOL_TABLE"
  | "INVESTIGATION_CASES_POOL_TABLE"
  | "AUDIT_EVENTS_POOL_TABLE"
  | "WORKLOAD_TABLE"
  | "DIVISION_BY_CSR"
  | "PROMO_CODES"
  | "PROMO_CODES_USAGE"
  | "WEB_PAYMENTS"
  | "USER_RATE_US_DATA"
  | "ADMIN_PERMISSIONS_TABLE"
  | "ADMIN"
  | "FLAGGED_DESCRIPTIONS_TABLE"
  | "BANNED_USERS_TABLE"
  | "SUPERVISOR_RECEIPT_CONFIGURATION"
  | "CURRENT_RECEIPT_MONTH"
  | "DATA_INPUT"
  | "PREVIOUS_RECEIPTS_MONTHS"
  | "KHATTABAT_ADMIN_REMUNERATIONS_PER_CYCLE"
  | "DATA_COLLECTION"
  | "FEATURES"
  | "KHATTABAT_CAPACITY"
  | "FUNNEL_STATS_DATA"
  | "MATCHMAKING_AUDIT_DATA"
  | "ACCOUNTS_DELETION_DATA"
  | "IOS_RECEIPT_TOKEN_DATA"
  | "FLAGGED_CONNECTIONS_HISTORY"
  | "FLAGGED_CONNECTIONS_DETAILS"
  | "ON_CALL_TEAM"
  | "BLOCKED_REQUESTS_HISTORY"
  | "BLOCKED_REQUESTS_DETAILS"
  | "USERS_DATA"
  | "USERS_PURCHASE_AND_SUBSCRIPTIONS"
  | "ON_CALL"
  | "PAYMENT_PACKAGES_FOR_LAST_DAY"
  | "PACKAGES_NOTIFICATION_TYPE_COUNT_FOR_LAST_DAY"
  | "ALARMS"
  | "DEBUG_IMAGE_AI"
  | "NEW_USERS_ALERT_MODE"
  | "FB_USERS_ALERT_MODE"
  | "SMS_USERS_ALERT_MODE"
  | "EMAIL_USERS_ALERT_MODE"
  | "EMAIL_SENT_ALERT_MODE"
  | "UNSUBSCRIBED_USER_ACTIVITY_EMAILS_ALERT_MODE"
  | "UNSUBSCRIBED_PROMOTIONAL_EMAILS_ALERT_MODE"
  | "UNSUBSCRIBED_NEW_MESSAGES_EMAILS_ALERT_MODE"
  | "UNSUBSCRIBED_VISIT_PROFILE_EMAILS_ALERT_MODE"
  | "UNSUBSCRIBED_LIKE_EMAILS_ALERT_MODE"
  | "LAST_LOGIN_USERS"
  | "ANDROID_PAYMENTS_ALERT_MODE"
  | "IOS_PAYMENTS_ALERT_MODE"
  | "RATE_LIMIT_ALERT_MODE"
  | "FLAGGED_CONNECTIONS_ALERT_MODE"
  | "SUB_TAB_ACCESS_SUMMARY"
  | "GENERATED_TABLES_SUMMARY"
  | "INVESTIGATION_CASES"
  | "REPORT_ACTIONS"
  | "REPORT_ACTION_REASONS"
  | "USER_CONSUMABLE_TABLE"
  | "OTHER_SUBSCRIPTION_HISTORY_FOR";

export type TTBWExtraData = {
  userID: number;
  userUUID: string;
  reportedUUID: string;
  reportID: number;
  reportedID: number;
  secondPartyID: number;
};

export type TTBWReasonType =
  | "BANNED"
  | "TEMPORARY_BANNED"
  | "SILENT_BANNED"
  | "WARNING"
  | "AUDIT_REASON"
  | "INVESTIGATE_REASON"
  | "";

export type TTBWReason = {
  id: number;
  tbwCategoryID: number;
  tbwName: string;
  tbwNameEnglish: string;
  tbwNameArabic: string;
  tbwNameFrench: string;
  tbwNameTurkish: string;
  tbwCategoryName: string;
  isActive: number;
};

export type UserEvent = {
  adminName: string;
  type: string;
  extraData: string;
  date: string;
  reportID: number;
  investigationCaseID: number;
  temporaryBanUntil: string;
  temporaryBanNumberOfDays: number;
  description: string;
  pageName: string;
  tbw_reasons: string[];
};

export type TInvestigationCases = {
  id: number;
  CSR: string;
  user_id: number;
  userUUID: string;
  gender: TGender;
  action_type: "banned" | "temporary_banned" | "warning" | "";
  action_place:
    | "USER_ADVANCED"
    | "MESSAGES"
    | "REPORT"
    | "CONVERSATIONS"
    | "REPORTS"
    | "";
  created_on: string;
  created_from: string;
  action_id: number;
  TBW_ids: string;
  userUnderCSRReport: number;
  isSilentBanned: number;
  reasons: string[];
};

export type TUserInvestigationCase = {
  investigationCase: TInvestigationCases[];
  pool: TPoolTable;
};

export type TSpentTime = {
  pageName: TCorePage;
  startTime: Date;
  endTime: Date;
  timeSpentInMillisecondsClient: number;
};

export type TAlbum = {
  id: string;
  imageSource: string;
  profilePictureStatus: string;
  selfieVerified: string;
  isActive: boolean;
};

export type TComment = {
  adminName: string;
  adminComment: string;
  commentDate: string;
};

export type TIssueType = "no_issue" | "major" | "minor";

export type TWorkloadDataResponse = {
  workload: {
    type: string;
    incoming: number;
    outgoingByCSRTotal: number;
    outgoingByAI: number;
  }[];
  totalWorkLoadByCSRs: {
    adminName: string;
    descriptionCount: number;
    descriptionPercentage: string;
    peerDescriptionCount: number;
    peerDescriptionPercentage: string;
    pictureCount: number;
    picturePercentage: string;
    reportCount: number;
    reportPercentage: string;
    investigationCount: number;
    investigationPercentage: string;
  }[];
};
export type TWorkloadData = {
  type: string;
  incoming: number;
  outgoingByCSRTotal: number;
  outgoingByAI: number;
};
export type TReceiptData = {
  data: {
    id: string;
    packageID: string;
    createdOn: string;
    expiryDate: string;
    status: string;
    hasPending: string;
    pendingPackage: string;
    pendingReason: string;
  };
};

export type TReportRKey =
  | "REPORT_REASON_1"
  | "REPORT_REASON_2"
  | "REPORT_REASON_3"
  | "REPORT_REASON_4"
  | "REPORT_REASON_5";

export type TAdminPermissions = {
  moduleId: number;
  permissionName: string;
  permissionDescription: string;
  hasPermission: 0 | 1;
};

export type TUserLoginData = {
  operatingSystem: string;
  createdOn: string;
};

export type TEffectivenessData = {
  id: string;
  event_name: string;

  audited_events: {
    issueType: "major" | "minor" | "all";
    eventName: string;
    textValue: number;
  };
  minor_errors: {
    issueType: "major" | "minor" | "all";
    eventName: string;
    textValue: number;
  };
  major_errors: {
    issueType: "major" | "minor" | "all";
    eventName: string;
    textValue: number;
  };
  total_issues: number;
  minor_errors_percentage: number;
  major_errors_percentage: number;
  total_errors_percentage: number;
};

export type TAuditPerformance = {
  auditName: string;
  minor: number;
  major: number;
  no_issue: number;
  total: number;
};

export type TAccountDeletionData = {
  id: number;
  userId: number;
  ratingState: number;
  reason: number;
  comment: string;
  date: Date;
};

export type TAlertLogType = {
  id: number;
  reasonId: number;
  reason: string;
  createdOn: string;
}[];

export type TAlertLogs = {
  NEW_USER_LOG_ISSUE: TAlertLogType;
  FB_USER_LOG_ISSUE: TAlertLogType;
  SMS_USER_LOG_ISSUE: TAlertLogType;
  EMAIL_USER_LOG_ISSUE: TAlertLogType;
  LAST_LOGIN_LOG_ISSUE: TAlertLogType;
  ANDROID_PAYMENTS: TAlertLogType;
  IOS_PAYMENTS: TAlertLogType;
  FLAGGED_CONNECTIONS: TAlertLogType;
  RATE_LIMIT: TAlertLogType;
  EMAIL_SENT: TAlertLogType;
  UNSUBSCRIBED_USER_ACTIVITY_EMAILS: TAlertLogType;
  UNSUBSCRIBED_PROMOTIONAL_EMAILS: TAlertLogType;
  UNSUBSCRIBED_NEW_MESSAGE_EMAILS: TAlertLogType;
  UNSUBSCRIBED_VISIT_PROFILE_EMAILS: TAlertLogType;
  UNSUBSCRIBED_LIKE_EMAILS: TAlertLogType;
};

export type TAlertData = {
  newUsersAlert: boolean;
  fbUsersAlert: boolean;
  smsUsersAlert: boolean;
  emailUsersAlert: boolean;
  lastLoginUsersAlert: boolean;
  androidPaymentsAlert: boolean;
  iosPaymentsAlert: boolean;
  flaggedConnectionAlert: boolean;
  flaggedConnectionAppAlert: boolean;
  flaggedConnectionWebAlert: boolean;
  flaggedConnectionAdminAlert: boolean;
  rateLimitBlockedAlert: boolean;
  rateLimitBlockedAppAlert: boolean;
  rateLimitBlockedWebAlert: boolean;
  rateLimitBlockedAdminAlert: boolean;
  alertDate: number;
  lastAlarmsCheckDateAlert: boolean;
  sentEmailsAlert: boolean;
  ipGeolocationAlert: boolean;
  unsubscribedUserActivityEmailsAlert: boolean;
  unsubscribedPromotionalEmailsAlert: boolean;
  unsubscribedNewMessageEmailsAlert: boolean;
  unsubscribedVisitProfileEmailsAlert: boolean;
  unsubscribedLikeEmailsAlert: boolean;
  alertLogs: TAlertLogs;
  bpUserAlert: boolean;
};

export type TUserReferralCodeUsageData = {
  createdOn: string;
  peerId: number;
  ownerPlatform: string;
  referralCode: StringOrNumber;
  ownerReward: string;
  codeValidityOrReason: string;
  userId: number;
  userPlatform: string;
  userReward: string;
};

export type TOnCallData = {
  id: number;
  real_date: string;
  new_users: number;
  new_users_increase: string;
  total_users: number;

  new_subscription: number;
  new_subscription_increase: string;
  android_subscription: number;
  ios_subscription: number;

  new_renewals: number;
  android_renewals: number;
  ios_renewals: number;
  web_renewals: number;
  email_campaign_count: number;
}[];

export type TUsersData = {
  id: number;
  realDate: string;
  newUsers: number;
  newUsersIncrease: string;
  totalUsers: number;
  flaggedConnections: number;
  rateLimitBlocked: number;
}[];

export type TUsersSubscriptionsAndPurchases = {
  id: number;
  newOneTimePurchases: number;
  newOneTimePurchasesIncrease: string;
  androidOneTimePurchases: number;
  iosOneTimePurchases: number;
  newSubscription: number;
  newSubscriptionIncrease: string;
  androidSubscription: number;
  iosSubscription: number;
  webSubscription: number;
  newRenewals: number;
  androidRenewals: number;
  iosRenewals: number;
  webRenewals: number;
}[];

export type TOnCallPaymentPackages = {
  date: string;
  bundleCode: string;
  platform: string;
  purchaseCount: number;
  renewCount: number;
  bundleDescription: string;
  period: string;
}[];
export type TOnCallPaymentPackagesToday = {
  date: string;
  bundleCode: string;
  platform: string;
  purchaseCount: number;
  renewCount: number;
  bundleDescription: string;
  period: string;
}[];

export type TOnCallPackageNotificationTypeCount = {
  date: string;
  bundleNotification: string;
  bundleNotificationCount: number;
}[];

export type TStatsAll = {
  usersData: TUsersData;
  usersPurchasesAndSubscriptions: TUsersSubscriptionsAndPurchases;
  onCallData: TOnCallData;
  paymentPackages: TOnCallPaymentPackages;
  paymentPackagesToday: TOnCallPaymentPackagesToday;
  packageNotificationTypeCount: TOnCallPackageNotificationTypeCount;
};

export type TFlaggedConnectionsData = {
  id: number;
  real_date: string;
  flagged_connections: number;
}[];

export type TFlaggedConnectionsDetails = {
  id: number;
  user_id: number;
  extra_params: string;
  error: string;
  route_name: string;
  created_on: string;
}[];

export type TFlaggedConnectionsAll = {
  flaggedConnectionsData: TFlaggedConnectionsData;
  flaggedConnectionsDetails: TFlaggedConnectionsDetails;
};

export type TAlarmsSetOff = {
  id: number;
  reason_id: number;
  reason: string;
  resolved: number;
  created_on: string;
  updated_on: string;
}[];

export type TUserSubscriptionPaymentHistory = {
  userID: number;
  transactionID: string;
  platform: string;
  packagePlatform: string;
  country: string;
  paymentMethod: string;
  subscriptionType: string;
  createdOn: string;
  expiryDate: string;
  amount: number | string;
  bundleCode: string;
  subscriptionStatus: string;
  description: string;
}[];

export type TDebugAIImageCheckerData = {
  id: number;
  status: string;
  reason: string;
  platform: string;
  label_caught: string;
  confidence_caught: number;
  picture_url: string;
}[];

export type TDebugAIImageCompareData = {
  id: number;
  user_id: number;
  matchResult: string;
  similarity: number;
  selfiePicURL: string;
  pictureToBeComparedURL: string;
  debug_data: string;
}[];

export type TUserAIDescriptionsData = {
  id: number;
  userId: number;
  gptDescription: string | null;
  claudeDescription: string | null;
  finalDescription: string;
  gptModel: string | null;
  claudeModel: string | null;
  gptMatchPercentage: number | null;
  claudeMatchPercentage: number | null;
}[];

export type TAllUsersAIDescriptionsData = {
  id: number;
  userId: number;
  usage: string;
  gptDescription: string | null;
  claudeDescription: string | null;
  finalDescription: string;
  gptModel: string | null;
  claudeModel: string | null;
  gptMatchPercentage: number | null;
  claudeMatchPercentage: number | null;
}[];
export type TPermissionID =
  | 0
  | 39
  | 40
  | 41
  | 42
  | 43
  | 44
  | 45
  | 46
  | 47
  | 48
  | 49
  | 50
  | 51
  | 52
  | 53
  | 3
  | -999999;

export type TPermissionName =
  | "Alarms"
  | "User"
  | "Approve-Reject"
  | "Reports"
  | "Investigation-Case"
  | "CSR"
  | "Auditor"
  | "Supervisor"
  | "ShareHolder"
  | "Developer"
  | "Communications"
  | "NO_PERMISSION_NEEDED"
  | "QA"
  | "Khatabat"
  | "Khattabat"
  | "SCSR"
  | "User-Messages"
  | "";

export enum TPermissions {
  "Alarms" = "Alarms",
  "User" = "User",
  "Approve-Reject" = "Approve-Reject",
  "Reports" = "Reports",
  "Investigation-Case" = "Investigation-Case",
  "CSR" = "CSR",
  "Auditor" = "Auditor",
  "Supervisor" = "Supervisor",
  "ShareHolder" = "ShareHolder",
  "QA" = "QA",
  "Developer" = "Developer",
  "Communications" = "Communications",
  "NO_PERMISSION_NEEDED" = "NO_PERMISSION_NEEDED",
  "Khatabat" = "Khatabat",
  "Khattabat" = "Khattabat",
  "SCSR" = "SCSR"
}

export type TDataREcatJSChart = {
  chartTitle: string;
  chartLabel: string[];
  chartYAxis: number[][];
  chartXAxis: string[];
  chartColors: string[];
  showEmpty: boolean;
};

export type TWatchAlertsData = {
  newUsers: number;
  fbUsers: number;
  smsUsers: number;
  emailUsers: number;
  lastLoginUsers: number;
  androidPayments: number;
  iosPayments: number;
  hyperpayIosPayments: number;
  hyperpayAndroidPayments: number;
  flaggedConnection: number;
  rateLimitBlocked: number;
  alertDate: string;
  unsubscribedUserActivityEmails: number;
  unsubscribedPromotionalEmails: number;
  unsubscribedNewMessageEmails: number;
  unsubscribedVisitProfileEmails: number;
  unsubscribedLikeEmails: number;
}[];

export type TStatus = "pending" | "resolved" | "warning" | "banned";

export type TReportCreatedFromType = "all" | "indonesia_malaysia" | "other";

export type TBWTypes =
  | "banned"
  | "temporary_banned"
  | "silent_banned"
  | "warning"
  | ""
  | "investigate"
  | "flagged";

export type TSilentBannedUser = {
  userId: number;
  userUUID: string;
  username: string;
  status: string;
  statusChangeDate: string;
  createdOn: string;
};

export type TUserStatus =
  | ""
  | "active"
  | "deactivated"
  | "banned"
  | "temporary_banned"
  | "silent_banned"
  | "hidden";

export type TPictureActiveStatus = "active" | "inactive";

export type TAuditEvent = {
  id: number;
  user_id: number;
  audited_by: string;
  action_by: string;
  type: string;
  extra_data: string;
  image_source: string;
  description: string;
  report_id: number;
  investigation_case_id: number;
  temporary_ban_number_of_days: number;
  temporary_ban_until: string;
  seen_by: number;
  page_name: string;
  audit_issue_type: string;
  audited_on: string;
  created_on: string;
  event_done_by: string;
  proof: string;
  reasons: string[];
  gender: string;
};

export type TSupervisedAuditEvent = TAuditEvent & {
  supervised_on: string;
  issueTypeByCSR: string;
  issueTypeBySupervisor: string;
  supervised_by: string;
  proof: string;
  reasons: string[];
};
export type TEt3Badge =
  | "REPORTS"
  | "APPROVE"
  | "INVESTIGATION_CASES"
  | "DEFAULT"
  | "PAID"
  | "NOT_PAID";

export type TPromoCodes = {
  adminName: string;
  promoCodeId: number;
  promocode: string;
  description: string;
  benefitInHours: number;
  startTime: Date;
  expiryTime: Date;
  maxUsage: number;
  promoCodeForGender: string;
  promoCodeForPaid: number;
  promoCodeForNationality: string;
  isActive: number;
  updatedOn: string;
  createdOn: string;
  benefitType: TRewardBenefitType;
}[];

export type TUserEmailEvent = {
  email: string;
  date: string;
  verified: string;
};

export type TAlbumItem = {
  picture: TAlbum;
  index: number;
};

export type TUserDeactivations = {
  type: string;
  date: string;
};

type TTablesIdsAuditorKeys =
  | "AUDIT_EVENTS_COUNT"
  | "AUDIT_EVENTS_LIST"
  | "AUDITED_EVENTS_COUNT"
  | "CSR_PERFORMANCE"
  | "CSR_EFFECTIVENESS"
  | "EVENTS_CHARTS_DAILY_STATS"
  | "MY_AUDITING_MISTAKES_EVENTS_COUNT"
  | "MY_AUDITING_PERFORMANCE_AUDIT_EFFECTIVENESS"
  | "MY_AUDITING_PERFORMANCE_AUDIT_PERFORMANCE"
  | "APPROVAL_POOL"
  | "REPORT_POOL"
  | "PROMO_CODE_USAGE_HISTORY"
  | "PROMO_CODES"
  | "TBW_REASONS"
  | "WORKLOAD_DATA_BY_CSR"
  | "WORKLOAD_DATA"
  | "INVESTIGATION_CASES_POOL"
  | "AUDIT_EVENTS_POOL";

type TTablesIdsInvestigationCasesKeys = "INVESTIGATION_CASES";

type TTablesIdsUserKeys =
  | "USER_ADVANCED_DATA"
  | "USER_ADMIN_COMMENTS"
  | "USER_DEVICES"
  | "USER_EDIT_PROFILE"
  | "USER_EDIT_PROFILE_ARCHIVE"
  | "USER_EVENTS_BREACHES_DATA"
  | "USER_EVENTS_USER_DEACTIVATIONS"
  | "USER_EVENTS"
  | "USER_EVENTS_EMAIL_EVENTS"
  | "USER_LOGIN_DATA";

export type TTablesIds = {
  AUDITOR: {
    [key in TTablesIdsAuditorKeys]: string;
  };

  INVESTIGATION_CASES: {
    [key in TTablesIdsInvestigationCasesKeys]: string;
  };

  USER: {
    [key in TTablesIdsUserKeys]: string;
  };
};

export type TPoolTable =
  | "APPROVAL"
  | "REPORT"
  | "INVESTIGATION_CASES"
  | "AUDITOR_AUDIT_EVENTS"
  | "";

export type TSearchType = "criteria_search" | "option_search" | "";

export type TAdvancedUserDescription = {
  descriptionID: number;
  descriptionTitle: Ti18nKeys;
  description: string;
  descriptionStatus: string;
  descriptionLanguage: string;
};

export type TDescriptionType =
  | "peer_description"
  | "self_description"
  | "not_description";

export type TDataApproveRejectAction =
  | "approve_picture"
  | "reject_picture"
  | "approve_description"
  | "reject_description"
  | "approve_peer_description"
  | "reject_peer_description"
  | "approve_picture_album"
  | "reject_picture_album";

export type TEventsPickerType =
  | "report"
  | "approval_description"
  | "approval_peer_description"
  | "approval_picture"
  | "investigation_cases";

export type TAdminVersionStates = {
  isActive: boolean;
  isUnderMaintenance: boolean;
  latestAdminVersion: string;
};
export type TMessagesType =
  | "text"
  | "sticker"
  | "khotabe_message"
  | "voice"
  | "picture"
  | "all";

export type TBrowsersTypes =
  | "Opera"
  | "Edge"
  | "Chrome"
  | "Safari"
  | "Firefox"
  | "IE"
  | "UNKNOWN";

export type TAdminPools = {
  adminId: number;
  fullName: string;
  totalCount: number;
}[];

export type TAdminResetPassword = {
  adminId: number;
  fullname: string;
  role: string;
}[];

export type TOnCallTeams = "SE" | "QA" | "CTO" | "CEO";

export type TOnCallTeamMembersArray = {
  id: number;
  team: TOnCallTeams;
  name: string;
  phoneNumber: string;
  email: string;
  onDuty: number;
}[];

export type TBlockRequestsData = {
  id: number;
  real_date: string;
  rate_limit_blocked: number;
}[];

export type TBlockRequestsDetails = {
  id: number;
  userID: number;
  createdOn: string;
  routeName: string;
  source: string;
  deviceID: string;
  displayDeviceID: string;
  ipAddress: string;
  extraParameters: string;
}[];

export type TBlockRequestsAll = {
  blockRequestsData: TBlockRequestsData;
  blockRequestsDetails: TBlockRequestsDetails;
};

export type TUserVipTimeType = "DAYS" | "HOURS" | "MINUTES";

export enum E_MESSAGE_DELETION_STATUS {
  NOT_DELETED = 0,
  HIDDEN_FROM_ME = 1,
  HIDDEN_FROM_EVERYONE = 2
}

export type TMessageReaction = {
  messageId: number;
  senderId: number;
  reaction: string;
  reactedOn: string;
  isSuppressed: number;
};

export type TReactionAction = "ADDED" | "CHANGED" | "REMOVED";

export type TMessageReactionEvent = {
  senderId: number;
  reaction: string;
  action: TReactionAction;
  happenedOn: string;
  isCurrent: boolean;
  isSuppressed: number;
};

export type TUserMessagesObject = {
  id: number;
  messageId: number;
  senderId: number;
  recipientId: number;
  userMessagesRecipientId: { senderId: number; recipientId: number };
  messageObject: {
    message: string;
    messageType: string;
  };
  sentOn: string;
  isDeleted: E_MESSAGE_DELETION_STATUS;
  deletedOn: string;
  reactions: TMessageReaction[];
  hasReactionHistory: boolean;
};

export type TUserMessagesDataCounts = {
  text: string;
  count: number;
  messagesCount: number;
}[];

export type TSeachedUser = {
  userID: number;
  userGender: string;
};

export enum Gender {
  "Male" = "m",
  "Female" = "f"
}

export type TFlaggedDescriptionsArray = {
  id: number;
  description: string;
  descriptionPercentage: number;
  adminName: string;
  createdOn: string;
  deletedID: number;
}[];

export type TBannedUsersArray = {
  id: number;
  userID: number;
  bannedDateAndTime: string;
  bannedUserDescription: {
    text: string;
    isFlagged: boolean;
  };
  bannedUserPeerDescription: {
    text: string;
    isFlagged: boolean;
  };
  userName: string;
  createdFrom: string;
  originCountry: string;
  residenceCountry: string;
}[];

export type TGetKhattabaClients = {
  isBlocked: number;
  isDeleted: number;
  createdOn: string;
  oneOOneCId: string;
  alkhattabaUUID: string;
  vipAlkhattabatStatus: TVIPKhattabatStatus;
  userUUID: string;
  userID: number;
  userName: string;
  originCountry: string;
  residenceCoutry: string;
  originCountryCode: string;
  residenceCountryCode: string;
  height: number;
  weight: number;
  age: number;
  lastLogin: string;
  marriageType: string;
  unreadOneOOneCMessages: number;
  oneOTwoCounter: number;
  hasActiveOneOTwo: boolean;
  isOneOOneCHiddenByKhattaba: boolean;
  isRenewedInLastSevenDays: boolean;
  hasBeenReactivated: number;
  reactivatedAt: Date;
  applicationStatus: TKhattabatApplicationStatus;
  hasUnreadMessagesSinceMoreThan18Hours: boolean;
  hasNoRCMessageSinceMoreThan54Hours: boolean;
  clientIsAlmostExpiredAndNo102Quality: boolean;
  isInItroductoryPhase: boolean;
};

export type TVIPKhattabatStatus = "ACTIVE" | "ALMOST_EXPIRED" | "EXPIRED";

export type TGetRateUsData = {
  id: number;
  countPositive: number;
  countNegative: number;
  total: number;
  date: string;
  percentagePositive: number;
  percentageNegative: number;
}[];

export type TConversationPropKeys =
  | "isOpened"
  | "messageField"
  | "insertUserIdField";
export type TConversationsProps = {
  isOpened: "OPENED" | "CLOSED";
  messageField: string;
  insertUserIdField: number;
};

export type TKhattabatMessage = {
  isKhattabaMessage: 1 | 0;
  isClientMessage: 1 | 0;
  message: string;
  messageType: string;
  createdOn: string;
  isSeen: 1 | 0;
  senderGender: TGender;
  conversationId: string;
};

export type TWebPaymentsObject = {
  id: number;
  userID: string;
  email: string;
  name: string;
  referenceID: string;
  productID: string;
  typeOfPayment: string;
  userCountryCode: string;
  expiryDate: string;
  bundlePrice: string;
  bundleCurrency: string;
  paymentStatus: EHyperpayPaymentStatus;
  createdOn: string;
  refundOrCancel: {
    referenceID: string;
    userIDToUseInVIP: number;
    paymentColumnStatus: EHyperpayPaymentStatus;
    isActiveSubscription: boolean;
  };
};
export type TalkhattabatConversationType =
  | "101C"
  | "102"
  | "101M"
  | "101C_COMMENTS"
  | "101M_COMMENTS";

export type TMediaType = "voice" | "picture";

export type TOneOTwoData = {
  [matchUsername in string]: {
    matchUserName: string;
    exitByMatch: 1 | 0;
    exitByUser: 1 | 0;
    exitedByMatchDate: Date;
    exitedByClientDate: Date;
    peerID: number;
    peerUUID: string;
    manualIsQuality: number;
    automaticIsQuality: number;
    messages: TKhattabatMessage[];
  };
};

export type TKhattabatConversations = {
  oneOOneCMessages: TKhattabatMessage[];
  oneOOneCComments: TKhattabatMessage[];
  oneOTwoMessages: TOneOTwoData;
  alKhattabaUUID: string;
  userUUID: string;
  userID: number;
  oneOOneCId: string;
};

export type TFeature = {
  featureId: number;
  name: string;
  is_active: number;
  gender: string;
  feature_rollout_percentage: TDbRolloutPercentage;
  feature_action: { id: number; is_active: number; gender: string };
};

export type TDbRolloutPercentage =
  | "10"
  | "20"
  | "30"
  | "40"
  | "50"
  | "60"
  | "70"
  | "80"
  | "90"
  | "100";

export type TWebPaidAllData = {
  userID: number;
  userCountryCode: string;
  bundleCode: string;
  bundlePrice: string;
  bundleCurrency: string;
  bundleExpiryDate: string;
  oti: string;
  iosAPPAccountToken: string;
};

export type TObtainReceiptData = {
  receipt: string;
}[];

export type TConfirmationPopUps =
  | "WEB_PAYMENT_CANCEL_POPUP"
  | "WEB_PAYMENT_REFUND_POPUP"
  | "WEB_PAYMENT_PREVIOUS_REFUND_POPUP"
  | "RESET_ACCOUNT_POPUP"
  | "REMOVE_SILENT_BAN_POPUP"
  | "RESET_PASSWORD_POPUP"
  | "ADD_COMMENT_POPUP"
  | "CLEAN_POOL_CONFIRMATION_POPUP"
  | "ONE_O_TWO_CONFIRMATION_POPUP"
  | "KHATTABAT_CLIENT_ASSIGN_CONFIRMATION_POPUP"
  | "REACTIVATE_KHATTABA_CLIENT_POPUP"
  | "GIVE_SUBSCRIPTION_CONFIRMATION_POPUP"
  | "TRANSFER_USER_PACKAGE_POPUP"
  | "EMPTY_STRING";

export type TKhattabatClientCategory = "PAID";

export type TPaidUsersInformation = {
  alkhatabaUUID: string;
  userUUID: string;
  userId: number;
  userName: string;
  gender: TGender;
  userStatus: string;
  isActivePaidUser: number;
  totalUnreadMessages: number;
};

export type TMatchDetail = {
  matchID: number;
  matchStatus: string;
  matchUserName: string;
  matchUUID: string;
  matchResidence: string;
  matchNationality: string;
  matchAge: number;
  matchHeight: number;
  matchWeight: number;
  matchLastLogin: string;
  marriageType: string;
  messages: TKhattabatMessage[];
  matchUnreadMessages: number;
  matchConvesationIsDisabled: boolean;
  isOpenedConversation: boolean;
  matchConversationIsHighlighted: boolean;
  matchHasDeletedTheConversation: boolean;
  oneOOneMComments: TKhattabatMessage[];
};

export type TMatchesConversations = TMatchDetail[];

export type TMatchDetailKeys = keyof TMatchDetail;

export type TSelectionPages = "clientPage" | "matchResultPage";

export type TKeyEntries = {
  label: string;
  value: string;
};

export type TCSRAdmins = {
  adminID: number;
  fullname: string;
};

export type TReceiptCompensationTitles =
  | "description"
  | "picture"
  | "report"
  | "investigationCases";

export type TIssuesCountKeys =
  | "descriptionIssuesCount"
  | "pictureIssuesCount"
  | "reportIssuesCount"
  | "investigationCaseIssuesCount";

export type TAuditedEventCountKeys =
  | "descriptionAuditedCount"
  | "pictureAuditedCount"
  | "reportAuditedCount"
  | "investigationCasesAuditedCount";

export type TEventCountKeys =
  | "descriptionEventCount"
  | "pictureEventCount"
  | "reportEventCount"
  | "investigationCasesEventCount";

export type TReceiptsConfigurationKeys =
  | "descriptionThreshold"
  | "descriptionDiscount"
  | "descriptionPricing"
  | "pictureThreshold"
  | "pictureDiscount"
  | "picturePricing"
  | "reportThreshold"
  | "reportDiscount"
  | "reportPricing"
  | "investigationCasesThreshold"
  | "investigationCasesDiscount"
  | "investigationCasesPricing";

export type TAdminIssuesCounts = {
  adminID: number;
  descriptionIssuesCount: number;
  pictureIssuesCount: number;
  reportIssuesCount: number;
  investigationCaseIssuesCount: number;
};

export type TDataInputObject = {
  adminID: number;
  descriptionIssuesCount: number;
  pictureIssuesCount: number;
  reportIssuesCount: number;
  investigationCaseIssuesCount: number;
  descriptionEventCount: number;
  pictureEventCount: number;
  reportEventCount: number;
  investigationCasesEventCount: number;
  descriptionAuditedCount: number;
  pictureAuditedCount: number;
  reportAuditedCount: number;
  investigationCasesAuditedCount: number;
};

export type TReceiptCompensation = {
  eventsCount: TEventsCount;
  auditedEventsCount: TAuditedEventsCount;
  adminIssuesCount: TAdminIssuesCounts;
  configurationReceipt: TConfigurationReceipt;
  adminFinalReceipts: TAdminFinalReceipts;
};

export type TEventsCount = {
  descriptionEventCount: number;
  pictureEventCount: number;
  reportEventCount: number;
  investigationCasesEventCount: number;
};

export type TAuditedEventsCount = {
  descriptionAuditedCount: number;
  pictureAuditedCount: number;
  reportAuditedCount: number;
  investigationCasesAuditedCount: number;
};

export type TAdminFinalReceipts = {
  id: number;
  adminID: number;
  descriptionPricing: number;
  descriptionDiscount: number;
  picturePricing: number;
  pictureDiscount: number;
  reportPricing: number;
  reportDiscount: number;
  investigationCasesPricing: number;
  investigationCasesDiscount: number;
  finalSalary: number;
  createdOn: string;
}[];

export type TNewlyMonthAdded = {
  adminID: number;
  finalSalary: number;
  descriptionPricing: number;
  descriptionDiscount: number;
  picturePricing: number;
  pictureDiscount: number;
  reportPricing: number;
  reportDiscount: number;
  investigationCasesPricing: number;
  investigationCasesDiscount: number;
};

export type TConfigurationReceipt = {
  [key in TReceiptCompensationTitles]: {
    discount: number;
    price: number;
    threshold: number;
  };
};

export type TCurrentMonth = {
  id: number;
  finalSalary: number;
  description: {
    price: number;
    discount: number;
  };
  picture: {
    price: number;
    discount: number;
  };
  report: {
    price: number;
    discount: number;
  };
  investigationCases: {
    price: number;
    discount: number;
  };
};

export type TReceiptsConfigurationData = {
  id: number;
  descriptionThreshold: StringOrNumber;
  descriptionDiscount: StringOrNumber;
  descriptionPricing: StringOrNumber;
  pictureThreshold: StringOrNumber;
  pictureDiscount: StringOrNumber;
  picturePricing: StringOrNumber;
  reportThreshold: StringOrNumber;
  reportDiscount: StringOrNumber;
  reportPricing: StringOrNumber;
  investigationCasesThreshold: StringOrNumber;
  investigationCasesDiscount: StringOrNumber;
  investigationCasesPricing: StringOrNumber;
  createdOn: string;
  [key: string]: StringOrNumber;
};

export type TReceiptsConfigurationInsertData = {
  descriptionThreshold: StringOrNumber;
  descriptionDiscount: StringOrNumber;
  descriptionPricing: StringOrNumber;
  pictureThreshold: StringOrNumber;
  pictureDiscount: StringOrNumber;
  picturePricing: StringOrNumber;
  reportThreshold: StringOrNumber;
  reportDiscount: StringOrNumber;
  reportPricing: StringOrNumber;
  investigationCasesThreshold: StringOrNumber;
  investigationCasesDiscount: StringOrNumber;
  investigationCasesPricing: StringOrNumber;
  [key: string]: StringOrNumber;
};

export type TOneOOneMEnablementInformation = {
  newIsBlockedState: number;
  alkhattabaUUID: string;
  userUUID: string;
  userID: number;
}[];

export type TEventName = "warn" | "ban" | "temporary_ban" | "silent_ban" | "";

export type TUserQuestionsAnswers = {
  questionId: number;
  allowedAnswers: number;
  questionTextEnglish: string;
  answerTextEnglish: string;
};

export type TSubscriptionType =
  | "VIP"
  | "ALKHATTABAT"
  | "PLUS"
  | "SILVER"
  | "GOLD"
  | "PLATINUM"
  | "ADVANCED"
  | "BLACK";

// Only these packages can be moved between users. Deliberately narrower than
// TSubscriptionType: ALKHATTABAT and ADVANCED are rejected by the route validator.
export enum E_TRANSFER_PACKAGE_TYPE {
  VIP = "VIP",
  PLUS = "PLUS",
  SILVER = "SILVER",
  GOLD = "GOLD",
  PLATINUM = "PLATINUM",
  BLACK = "BLACK"
}

export type TTransferPackageType = `${E_TRANSFER_PACKAGE_TYPE}`;

export type TTransferUserPackageResult = {
  type: TTransferPackageType;
  package: string;
  fromUserId: number;
  toUserId: number;
  previousExpiry: number;
  newExpiry: number;
  sourceStatus: string;
  targetStatus: string;
};

export type TTransferConsumableCount = {
  featureCode: string;
  count: string;
};

export type TTransferUserPackagePreview = {
  type: TTransferPackageType;
  package: string;
  fromUserId: number;
  toUserId: number;
  currentExpiry: number;
  newExpiry: number;
  remainingQuotas: TTransferConsumableCount[];
  remainingBalances: TTransferConsumableCount[];
};

export type TAuditorEventsData = {
  adminName: string;
  auditedReports: number;
  totalReports: number;
  auditedDescriptions: number;
  totalDescriptions: number;
  auditedPeerDescriptions: number;
  totalPeerDescriptions: number;
  auditedPictures: number;
  totalPictures: number;
  auditedInvestigationCases: number;
  totalInvestigationCases: number;
}[];

export type TReactivateOneOTwoConversationProps = {
  conversationID: string;
  alkhattabaUUID: string;
  userUUID: string;
  oneOOneCId: string;
};

export type TKhattabatDataCollection = {
  khattabaName: string;
  totalActiveClients: number;
  totalClients: number;
  activeUsersWithoutOrLowQualityOneOTwo: number;
}[];

export type TKhattabatClientData = {
  userID: number;
  OS: "iOS" | "Android" | "Other";
  username: string;
  gender: TGender;
  khattaba: string;
  typeOfMarriage: "Regular" | "Poly" | "Misyar";
  age: number;
  nationality: string;
  location: string;
  city: string;
  createdOn: Date;
  firstPurchaseType: "Weekly" | "Monthly";
  firstPurchaseDate: Date;
  weeklyRefund: number;
  monthlyRefund: number;
  refund: number;
  renewWeekToMonth: number;
  renewMonthToMonth: number;
  renewal: number;
  cancelled: string;
  cancellationDate: Date;
  active: string;
  lastPurchaseDate: Date;
  oneOTwoNumber: number;
  oneOTwoQualityNumber: number;
  lastRenewalDate: string;
  latestSuccessfullRefundDate: string;
  vipUntilAlkhattabatDate: string;
}[];

export type TWebPaymentsSearchOptions =
  | "BY_USER_ID"
  | "BY_DATE"
  | "BY_EMAIL"
  | "BY_TRANSACTION_ID";

export type TKhattabatFunnelStatsData = {
  apply: number;
  toBeInformed: number;
  applied: number;
  doNotInform: number;
  accepted: number;
  rejected: number;
  deleted: number;
  ongoing: number;
  paying: number;
  cancelled: number;
  refunded: number;
  active: number;
};

export type TKhattabatMatchmakingSearchCriteria = {
  minAge: number;
  maxAge: number;
  minWeight: number;
  maxWeight: number;
  minHeight: number;
  maxHeight: number;
  countryCode: string[];
  nationalityCode: string[];
  isChosenNationalitiesExcluded: boolean;
  isChosenResidenceCountriesExcluded: boolean;
  typeOfMarriage: string[];
  residenceCity: string[];
  maritalStatus: string[];
  havingKids: string;
  work: string[];
  education: string[];
  femaleWorkOrStudy: string;
  smoking: string;
  belongsToATribe: string;
  relocation: string;
  skinColor: string[];
  religion: string[];
  veil: string[];
  income: string[];
  matchGender: string;
  matchAppLanguage: string;
  limit: number;
};

export type TKhattabatMatchmakingAuditRow = {
  khattabaName: string;
  requestedOn: string;
  clientUserId: number;
  matchesCount: number;
  acceptedCount: number;
  searchCriteria: TKhattabatMatchmakingSearchCriteria;
};

export type TAdminSubTabAccessData = {
  subTab: string;
  accessCount: number;
  latAccess: string;
  accessedBy: string;
}[];

export type TAdminGeneratedTablesData = {
  subTab: string;
  subTabTable: string;
  generationCount: number;
  lastGenerated: string;
  generatedBy: string;
}[];

export enum E_KHATTABAT_APPLICATION_STATUS {
  NONE = "none",
  CREATED = "created",
  INCOMPLETE = "incomplete",
  COMPLETED = "completed",
  PENDING = "pending",
  REJECTED = "rejected",
  APPROVED = "approved",
  EXPIRED = "expired",
  PAID = "paid",
  ACTIVE = "active",
  BREACH = "breach"
}

export type TKhattabatApplicationStatus = `${E_KHATTABAT_APPLICATION_STATUS}`;

export enum E_CLIENT_PAGE {
  ALL = "all",
  BREACHE_LEVEL = "breacheLevel"
}

export type TClientPageType = `${E_CLIENT_PAGE}`;
export type TReportAction = {
  gender: TGender;
  resolved: number;
  banned: number;
  tempBanOneDay: number;
  tempBanThreeDays: number;
  tempBanFiveDays: number;
  tempBanSevenDays: number;
  warned: number;
  total: number;
}[];

export type TTBWEventStats = {
  tbwName: string;
  banned: number;
  tempBanOneDay: number;
  tempBanThreeDays: number;
  tempBanFiveDays: number;
  tempBanSevenDays: number;
  warned: number;
  total: number;
}[];

export enum E_ALKHATTABAT_RATING_ANSWERS_IDS {
  VERY_HELPFUL = "0",
  HELPFUL = "1",
  BAD = "2"
}

export enum E_101C_AI_MARRIAGE_TYPE {
  ANY = "0",
  CIVIL = "1",
  REGULAR = "2",
  POLYGAMOUS = "3",
  MISYAR = "4"
}

export type TUserConsumablesData = {
  boostNextRenewalDate: string;
  rewindNextRenewalDate: string;
  readReceiptNextRenewalDate: string;
  chatWeeklyNextRenewalDate: string;
  chatDailyNextRenewalDate: string;
  payingBoostPackageUntil: string;
  payingRewindPackageUntil: string;
  meteredBoostCount: string;
  quotaBoostCount: string;
  meteredRewindCount: string;
  quotaRewindCount: string;
  meteredReadReceiptCount: string;
  quotaReadReceiptCount: string;
  meteredChatWeeklyCount: string;
  meteredChatDailyCount: string;
};
